import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useReadiness } from '@/lib/readiness/useReadiness'
import { useMigrationStore, useMigrationScope } from '@/stores/migrationStore'
import { fetchMigrationFacts } from '@/lib/migration/facts'
import { buildSummary } from '@/lib/migration/summary'
import { useEnsureMigrationScope } from '@/lib/migration/usePlanSummary'
import { isPlanLocked, usePlanActions, usePlanData } from '@/lib/migration/usePlan'

// Page hook for the submission planning screens: resolves the readiness
// context (org, index), reads the plan from qtz-readiness (usePlanData) and
// exposes its actions, keeps the tracker's browser scope beside it, builds the
// summary object every tile reads, and runs the sync (plan on the backend,
// tracker facts in the browser) with the daily readiness sync and on demand.

const DAY_MS = 86_400_000

export function useMigration() {
  const ctx = useReadiness()
  const { orgId, indexId, index, scope: rScope, actor, orgName } = ctx
  const enabled = !!index.submission // hidden for an index without a submission (QPI, I2 §11.3 as amended)
  const mscope = useMigrationScope(orgId, indexId)
  const syncStore = useMigrationStore((s) => s.sync)
  useEnsureMigrationScope(ctx)
  const plan = usePlanData(orgId, indexId, enabled)
  const actions = usePlanActions(orgId, indexId, actor)

  const [syncing, setSyncing] = useState(false)
  const sync = useCallback(async () => {
    if (syncing || !orgId) return
    setSyncing(true)
    try {
      await Promise.allSettled([
        actions.sync(),
        fetchMigrationFacts(orgId, rScope.settings.discovery_facts).then((facts) => syncStore(orgId, indexId, facts, actor)),
      ])
    } finally {
      setSyncing(false)
    }
  }, [syncing, orgId, indexId, actions, rScope.settings.discovery_facts, syncStore, actor])

  // Runs with the daily readiness sync: once per page load when the plan's
  // last sync is overdue, and once for a tracker scope that has never seen
  // the facts.
  const ran = useRef(false)
  useEffect(() => {
    if (ran.current || !orgId || !plan.plan) return
    const overdue = !plan.plan.last_sync_at || Date.now() - new Date(plan.plan.last_sync_at).getTime() > DAY_MS
    if (overdue || !mscope?.facts) {
      ran.current = true
      void sync()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.plan?.last_sync_at, orgId, indexId])

  // The template's Organisation name. The backend reads it from a profile
  // nothing writes yet, so an empty name is seeded once from the login's
  // organisation; it stays a manual field the person can change.
  const seeded = useRef<string | null>(null)
  useEffect(() => {
    const p = plan.plan
    if (!p || !orgName || p.org_name.trim() || isPlanLocked(p) || seeded.current === p.id) return
    seeded.current = p.id
    actions.updatePlanField('org_name', orgName)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.plan?.id, plan.plan?.org_name, orgName])

  const summary = useMemo(() => (enabled ? buildSummary(index, plan, mscope) : null), [enabled, index, plan, mscope])
  const locked = isPlanLocked(plan.plan)
  return { ...ctx, mscope, plan, actions, summary, locked, migrationSync: sync, migrationSyncing: syncing, enabled }
}

export type MigrationCtx = ReturnType<typeof useMigration>
